
import {
  computed, ComputedRef, defineComponent, onBeforeUnmount, onDeactivated, ref, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMainStore } from '@/store/mainStore';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import { IConversation, IConversationItemDirectList, IConversationItemGroupList } from '@/types/interfaces';
import { useMessagesStore } from '../store/messagesStore';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import { PHOTO_VIEW_TYPE } from '@/types/enums';

export default defineComponent({
  emits: ['open-group-info'],
  props: {
    conversation: { type: Object as () => IConversationItemDirectList | IConversationItemGroupList, required: true },
  },
  components: {
    AppLazyImage,
  },
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();
    const isSelected = ref(false);

    const isActive = computed(() => {
      if ((props.conversation as IConversationItemGroupList).groupId) {
        return (props.conversation as IConversationItemGroupList).groupId === route.params.id;
      }
      return (props.conversation as IConversationItemDirectList).partner.accountId === route.params.id;
    });

    const fullName = computed(() => {
      if ((props.conversation as IConversationItemDirectList)?.partner) {
        return `${
          (props.conversation as IConversationItemDirectList).partner.firstName} ${(props.conversation as IConversationItemDirectList).partner.lastName
        }`;
      }
      return (props.conversation as IConversationItemGroupList).group.name;
    });
    const date: ComputedRef<string> = computed(() => {
      const currentDate = new Date(Number(props.conversation.lastMessageTimestamp));

      const today = new Date();

      const isToday = (
        currentDate.getDate() === today.getDate()
        && currentDate.getMonth() === today.getMonth()
        && currentDate.getFullYear() === today.getFullYear()
      );

      const isThisYear = currentDate.getFullYear() === today.getFullYear();

      if (isToday) {
        return currentDate.toLocaleString('en-US', { hour: 'numeric', hour12: true, minute: 'numeric' });
      }
      if (isThisYear) {
        return currentDate.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
        });
      }
      return currentDate.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    });

    const photoUrl = computed(() => {
      if ((props.conversation as IConversationItemDirectList)?.partner?.photo?.url) {
        return lowResUrlByViewTypeByUrl((props.conversation as IConversationItemDirectList).partner.photo!.url, PHOTO_VIEW_TYPE.WHSML);
      }
      if ((props.conversation as IConversationItemGroupList)?.group?.photo?.url) {
        return lowResUrlByViewTypeByUrl((props.conversation as IConversationItemGroupList).group.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      if ((props.conversation as IConversationItemGroupList)?.group) {
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/Group.svg');
      }

      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    function messageContact(): void {
      if (!isActive.value) {
        messagesStore.currentConversation = { info: { name: fullName.value } } as IConversation;
      }
      isSelected.value = true;

      if ((props.conversation as IConversationItemDirectList)?.partner) {
        router.push(`/messages/${(props.conversation as IConversationItemDirectList)?.partner.accountId}`);
      } else {
        router.push(`/messages/group/${(props.conversation as IConversationItemGroupList)?.group.id}`);
      }
    }

    function viewProfile(): void {
      if ((props.conversation as IConversationItemDirectList)?.partner) {
        router.push(`/profile/${(props.conversation as IConversationItemDirectList)?.partner.accountId}`);
      } else if (mainStore.isMobile) {
        emit('open-group-info', props.conversation);
      } else {
        messageContact();
      }
    }

    watch(() => props.conversation, () => {
      isSelected.value = false;
    });

    return {
      fullName,
      photoUrl,
      date,
      messageContact,
      viewProfile,
      mainStore,
      isSelected,
      isActive,
    };
  },
});
